export type ReviewMark = 'same' | 'delete' | 'insert' | 'change';

export type ReviewPart = {
    text: string;
    mark: ReviewMark;
};

type Piece = {
    word: string;
    spaceBefore: string;
};

type Edit =
    | { type: 'equal'; before: Piece; after: Piece }
    | { type: 'delete'; before: Piece }
    | { type: 'insert'; after: Piece };

/**
 * Word diff for the QA card.
 * A replaced word is `change` (yellow). Words only on the left are `delete`
 * (red), words only on the right are `insert` (green). Spacing stays unmarked.
 */
export function diffReviewText(
    before: string,
    after: string,
): { left: ReviewPart[]; right: ReviewPart[] } {
    const leftTokens = tokenize(before);
    const rightTokens = tokenize(after);
    const edits = diffPieces(leftTokens.pieces, rightTokens.pieces);
    const paired = pairReplacements(edits);

    const left: ReviewPart[] = [];
    const right: ReviewPart[] = [];

    for (const edit of paired) {
        if (edit.type === 'equal' || edit.type === 'delete' || edit.type === 'change') {
            pushPiece(left, edit.before, edit.type === 'equal' ? 'same' : edit.type === 'delete' ? 'delete' : 'change');
        }
        if (edit.type === 'equal' || edit.type === 'insert' || edit.type === 'change') {
            pushPiece(right, edit.after, edit.type === 'equal' ? 'same' : edit.type === 'insert' ? 'insert' : 'change');
        }
    }

    if (leftTokens.tail) left.push({ text: leftTokens.tail, mark: 'same' });
    if (rightTokens.tail) right.push({ text: rightTokens.tail, mark: 'same' });

    return { left, right };
}

function pushPiece(parts: ReviewPart[], piece: Piece, mark: ReviewMark) {
    if (piece.spaceBefore) parts.push({ text: piece.spaceBefore, mark: 'same' });
    if (piece.word) parts.push({ text: piece.word, mark });
}

function tokenize(text: string): { pieces: Piece[]; tail: string } {
    const pieces: Piece[] = [];
    let end = 0;

    for (const match of text.matchAll(/(\s*)(\S+)/g)) {
        pieces.push({ spaceBefore: match[1], word: match[2] });
        end = (match.index ?? end) + match[0].length;
    }

    return { pieces, tail: text.slice(end) };
}

type Paired =
    | { type: 'equal'; before: Piece; after: Piece }
    | { type: 'delete'; before: Piece }
    | { type: 'insert'; after: Piece }
    | { type: 'change'; before: Piece; after: Piece };

/**
 * A run of deletions followed by insertions.
 * Same number of words: each one was replaced (yellow).
 * Different number: only a word that is still recognisable (a letter or two,
 * or punctuation stuck to it) is a replacement. The rest was removed or added,
 * so neighbours are not painted yellow just because they sit next to the edit.
 */
function pairReplacements(edits: Edit[]): Paired[] {
    const out: Paired[] = [];
    let index = 0;

    while (index < edits.length) {
        const edit = edits[index];
        if (edit.type === 'equal') {
            out.push(edit);
            index += 1;
            continue;
        }

        const deletes: Piece[] = [];
        const inserts: Piece[] = [];
        while (index < edits.length && edits[index].type === 'delete') {
            deletes.push((edits[index] as Extract<Edit, { type: 'delete' }>).before);
            index += 1;
        }
        while (index < edits.length && edits[index].type === 'insert') {
            inserts.push((edits[index] as Extract<Edit, { type: 'insert' }>).after);
            index += 1;
        }

        if (deletes.length === inserts.length) {
            for (let i = 0; i < deletes.length; i += 1) {
                out.push({ type: 'change', before: deletes[i], after: inserts[i] });
            }
            continue;
        }

        let insertAt = 0;
        for (const before of deletes) {
            let found = -1;
            for (let i = insertAt; i < inserts.length; i += 1) {
                if (similarWord(before.word, inserts[i].word)) {
                    found = i;
                    break;
                }
            }

            if (found < 0) {
                out.push({ type: 'delete', before });
                continue;
            }

            for (let i = insertAt; i < found; i += 1) {
                out.push({ type: 'insert', after: inserts[i] });
            }
            out.push({ type: 'change', before, after: inserts[found] });
            insertAt = found + 1;
        }

        for (let i = insertAt; i < inserts.length; i += 1) {
            out.push({ type: 'insert', after: inserts[i] });
        }
    }

    return out;
}

function wordCore(word: string): string {
    return word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').toLowerCase();
}

function similarWord(a: string, b: string): boolean {
    if (a === b) return true;
    const left = wordCore(a);
    const right = wordCore(b);
    if (left && left === right) return true;

    const from = left || a.toLowerCase();
    const to = right || b.toLowerCase();
    const dist = levenshtein(from, to);
    const longer = Math.max(from.length, to.length);
    return longer > 0 && dist <= 2 && dist / longer <= 0.4;
}

function levenshtein(a: string, b: string): number {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;

    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i += 1) {
        const next = [i];
        for (let j = 1; j <= b.length; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            next[j] = Math.min(next[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        }
        prev = next;
    }
    return prev[b.length];
}

/**
 * Myers diff on word tokens. Returns edits in source order.
 */
function diffPieces(a: Piece[], b: Piece[]): Edit[] {
    const n = a.length;
    const m = b.length;
    const max = n + m;
    const v = new Map<number, number>();
    v.set(1, 0);
    const trace: Map<number, number>[] = [];

    for (let d = 0; d <= max; d += 1) {
        trace.push(new Map(v));

        for (let k = -d; k <= d; k += 2) {
            const down = k === -d || (k !== d && (v.get(k - 1) ?? 0) < (v.get(k + 1) ?? 0));
            let x = down ? (v.get(k + 1) ?? 0) : (v.get(k - 1) ?? 0) + 1;
            let y = x - k;

            while (x < n && y < m && a[x].word === b[y].word) {
                x += 1;
                y += 1;
            }

            v.set(k, x);

            if (x >= n && y >= m) {
                return backtrack(trace, a, b, d);
            }
        }
    }

    return a.map((before) => ({ type: 'delete', before })).concat(
        b.map((after) => ({ type: 'insert', after })),
    );
}

function backtrack(trace: Map<number, number>[], a: Piece[], b: Piece[], dEnd: number): Edit[] {
    const edits: Edit[] = [];
    let x = a.length;
    let y = b.length;

    for (let d = dEnd; d > 0; d -= 1) {
        const v = trace[d];
        const k = x - y;
        const down = k === -d || (k !== d && (v.get(k - 1) ?? 0) < (v.get(k + 1) ?? 0));
        const prevK = down ? k + 1 : k - 1;
        const prevX = v.get(prevK) ?? 0;
        const prevY = prevX - prevK;

        while (x > prevX && y > prevY) {
            x -= 1;
            y -= 1;
            edits.push({ type: 'equal', before: a[x], after: b[y] });
        }

        if (x === prevX) {
            y -= 1;
            edits.push({ type: 'insert', after: b[y] });
        } else {
            x -= 1;
            edits.push({ type: 'delete', before: a[x] });
        }
    }

    while (x > 0 && y > 0) {
        x -= 1;
        y -= 1;
        edits.push({ type: 'equal', before: a[x], after: b[y] });
    }
    while (x > 0) {
        x -= 1;
        edits.push({ type: 'delete', before: a[x] });
    }
    while (y > 0) {
        y -= 1;
        edits.push({ type: 'insert', after: b[y] });
    }

    edits.reverse();
    return edits;
}
